import { SessionParticipantDto } from '@linka/domain/dtos';
import { useSessionParticipants } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { useParams } from 'react-router';
import { ColumnDef, DataTable } from '../../../data-table/data-table';
import { Breadcrumb } from '../breadcrumb';
import { ExportChartTriggerButton } from '../components';
import { ExportFlowWrapper } from '../export-flow-wrapper';
import { useSearchFilters } from '../hooks/use-search-filters';
import { useSystemFilters } from '../hooks/use-system-filters';

interface DetailedSessionsProps {
  userEmail?: string;
}

// Coluna "Sessão": `sessionTitle` já chega pronto da API, formatado na parede de São Paulo pelo
// mesmo TO_CHAR que alimenta a coluna "Sessão" do arquivo exportado — por isso a célula só
// imprime esse texto. Antes ela ainda concatenava `sessionStartDate` (ISO) formatado por
// `formatDate`, que usa o fuso do navegador: repetia a mesma data na célula e, em qualquer
// máquina fora de UTC-3, mostrava um horário diferente do que saía no xlsx/csv (#29543).
export const buildSessionColumn = (header: string): ColumnDef<SessionParticipantDto> => ({
  accessorKey: 'sessionTitle',
  header,
  cell: ({ row }) => <span>{row.original.sessionTitle}</span>,
});

export const DetailedSessions = ({ userEmail }: DetailedSessionsProps) => {
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const { setFilter } = useSearchFilters();
  const systemFilters = useSystemFilters();
  const { data: sessions, isPending } = useSessionParticipants(eventId);

  const columns: ColumnDef<SessionParticipantDto>[] = [
    {
      accessorKey: 'participantId',
      header: t('dashboard.detailed.participant-id'),
    },
    {
      accessorKey: 'participantName',
      header: t('dashboard.detailed.participant-name'),
    },
    {
      accessorKey: 'participantEmail',
      header: t('dashboard.detailed.participant-email'),
    },
    {
      accessorKey: 'participantPhone',
      header: t('dashboard.detailed.participant-phone'),
    },
    {
      accessorKey: 'ticketTitle',
      header: t('ticket'),
    },
    buildSessionColumn(t('dashboard.detailed.session-column')),
  ];

  return (
    <div>
      <Breadcrumb hideHome />
      <header className='grid sm:grid-cols-[1fr,max-content] items-center gap-y-mini my-xxs'>
        <div>
          <h1 className='ts-heading-sm sm:ts-heading-md text-ctx-content-title'>
            {t('dashboard.detailed.sessions-title')}
          </h1>
          <p className='ts-paragraph-xxs sm:ts-paragraph-xs text-ctx-content-base mt-nano'>
            {t('dashboard.detailed.sessions-description')}
          </p>
        </div>
        <ExportChartTriggerButton type='OVERVIEW_SESSIONS' className='col-span-2 self-end sm:col-span-1' />
      </header>
      <DataTable
        isPending={isPending}
        columns={columns}
        perPage={10}
        total={sessions?.total}
        data={sessions?.data || []}
        orderBy={systemFilters.orderBy}
        sortOrder={systemFilters.sortOrder}
        onSortChange={(col, dir) => setFilter({ orderBy: col, sortOrder: dir })}
        page={systemFilters.page}
        onPageChange={(page) => setFilter({ page: page.toString() })}
      />
      <ExportFlowWrapper userEmail={userEmail} />
    </div>
  );
};
